import { ContactsView } from "@/components/contact/ContactsView";
import { HistoryModal } from "@/components/history/HistoryModal";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import CopyToClipboardView from "@/components/views/CopyToClipboardView";
import HtmlView from "@/components/views/HtmlView";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import {
  formatAddress,
  formatAggregatedName,
  formatIban,
  formatLocaleDate,
  formatPhoneNumber,
} from "@/utils/formatUtils";
import { Button, Descriptions, Empty, Popconfirm, Skeleton } from "antd";
import { DescriptionsItemType } from "antd/es/descriptions";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteSecondaryAgent, useGetSecondaryAgent } from "../../../../common/api/queries";
import { Permission } from "../../../../common/security/enums";
import { UUID } from "../../../../typings/global";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { AgentSecondaryStatus, SecondaryAgentType } from "../../enums";
import { SecondaryAgentRepresentative } from "../../types";
import { isLegalSecondaryAgent, isNaturalSecondaryAgent, isSelfEmployedSecondaryAgent } from "../../utils";
import { AgentTypeTag } from "../tags/AgentTypeTag";
import { PersonalData } from "./PersonalData";
import { SecondaryAgentRepresentativeDrawer } from "./SecondaryAgentRepresentativeDrawer";

type Props = {
  onUpdate: () => void;
  agentId: UUID;
  agentSecondaryStatus?: AgentSecondaryStatus;
};

export const SecondaryAgentDetailView = ({ onUpdate, agentId, agentSecondaryStatus }: Props) => {
  const { t } = useTranslation();

  const [drawerRepresentative, setDrawerRepresentative] = useState<SecondaryAgentRepresentative>();
  const [historyModalIsOpen, setHistoryModalIsOpen] = useState(false);

  const { data, isFetching } = useGetSecondaryAgent(agentId);
  const deleteSecondaryAgent = useDeleteSecondaryAgent();

  const items = useMemo(() => {
    const items: DescriptionsItemType[] = [];

    if (!data) {
      return items;
    }

    items.push({
      label: t("agent.attrs.aggregatedName"),
      children: (
        <>
          <span>{data.aggregatedName}</span>
          <AgentTypeTag style={{ marginLeft: 8 }} type={data.type} />
        </>
      ),
    });

    if (isNaturalSecondaryAgent(data)) {
      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={data} />,
      });
    }

    if (isSelfEmployedSecondaryAgent(data)) {
      items.push({
        label: t("agent.attrs.companyName"),
        children: data.companyName,
      });

      items.push({
        label: t("agent.titles.personalData"),
        children: <PersonalData agent={data} />,
      });

      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 250 } }}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={data.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.districtOffice"),
                children: data.districtOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={data.taxId} />,
              },
              {
                label: t("agent.attrs.tradeRegisterNumber"),
                children: data.tradeRegisterNumber,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={data.vatId} />,
              },
            ]}
          />
        ),
      });
    }

    if (isLegalSecondaryAgent(data)) {
      items.push({
        label: t("agent.attrs.companyName"),
        children: data.companyName,
      });

      items.push({
        label: t("agent.titles.companyData"),
        children: (
          <Descriptions
            size="small"
            bordered
            styles={{ label: { width: 250 } }}
            column={2}
            items={[
              {
                label: t("agent.attrs.companyRegistrationNumber"),
                children: <CopyToClipboardView content={data.companyRegistrationNumber} />,
              },
              {
                label: t("agent.attrs.commercialRegisterDistrictOffice"),
                children: data.commercialRegisterDistrictOffice,
              },
              {
                label: t("agent.attrs.taxId"),
                children: <CopyToClipboardView content={data.taxId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterSection"),
                children: data.commercialRegisterSection,
              },
              {
                label: t("agent.attrs.vatId"),
                children: <CopyToClipboardView content={data.vatId} />,
              },
              {
                label: t("agent.attrs.commercialRegisterInsertionNumber"),
                children: data.commercialRegisterInsertionNumber,
              },
              {
                label: t("agent.attrs.legalTypeChangeDate"),
                children: data.legalTypeChangeDate ? formatLocaleDate(data.legalTypeChangeDate) : undefined,
              },
            ]}
          />
        ),
      });

      items.push({
        label: t("agent.attrs.representatives"),
        children: data.representatives.map((representative, index) => (
          <span key={representative.id}>
            <a onClick={() => setDrawerRepresentative(representative)}>{formatAggregatedName(representative)}</a>
            {index < data.representatives.length - 1 && ", "}
          </span>
        )),
      });
    }

    items.push({
      label: t("agent.titles.basicData"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: 250 } }}
          items={[
            {
              label: t("agent.attrs.email"),
              children: (
                <CopyToClipboardView content={data.email} wrapper={<a href={`mailto:${data.email}`}>{data.email}</a>} />
              ),
            },
            {
              label: t("agent.attrs.phone"),
              children: (
                <CopyToClipboardView
                  content={data.phone}
                  wrapper={<a href={`tel:${data.phone}`}>{formatPhoneNumber(data.phone)}</a>}
                />
              ),
            },
            {
              label: t("agent.attrs.web"),
              children: data.web,
            },
            {
              label: t("agent.attrs.bankAccountNumber"),
              children: (
                <CopyToClipboardView
                  content={data.bankAccountNumber ? formatIban(data.bankAccountNumber) : undefined}
                />
              ),
            },
            {
              label: t("agent.attrs.note"),
              children: data.note ? <HtmlView value={data.note} /> : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.addresses"),
      children: (
        <Descriptions
          size="small"
          bordered
          column={1}
          styles={{ label: { width: 250 } }}
          items={[
            {
              label:
                data.type === SecondaryAgentType.LEGAL ? t("agent.attrs.addressLongPO") : t("agent.attrs.addressLong"),
              children: formatAddress(data.address),
            },
            {
              label:
                data.type === SecondaryAgentType.SELF_EMPLOYED
                  ? t("agent.attrs.serviceAddressFOP")
                  : t("agent.attrs.serviceAddress"),
              children: data.serviceAddress ? formatAddress(data.serviceAddress) : undefined,
            },
            {
              label: t("agent.attrs.correspondenceAddress"),
              children: data.correspondenceAddress ? formatAddress(data.correspondenceAddress) : undefined,
            },
          ]}
        />
      ),
    });

    items.push({
      label: t("agent.titles.contacts"),
      children: <ContactsView contacts={data.contacts} />,
    });

    return items;
  }, [data]);

  if (isFetching) {
    return <Skeleton active />;
  }

  return data ? (
    <>
      <ItemCreatedUpdatedInfoView item={data} className="margin-top-tiny" />

      <Descriptions
        className="margin-top-medium"
        styles={{ label: { fontWeight: "bold", minWidth: 180 } }}
        bordered
        items={items}
        column={1}
        extra={
          data && (
            <>
              <Button icon={<AntIcon type="history" />} size="small" onClick={() => setHistoryModalIsOpen(true)}>
                {t("history.title")}
              </Button>

              <HistoryModal
                item={data}
                type="agent"
                translationRootPath="agent.attrs"
                isOpen={historyModalIsOpen}
                onCancel={() => setHistoryModalIsOpen(false)}
              />
            </>
          )
        }
      />

      <SecondaryAgentRepresentativeDrawer
        representative={drawerRepresentative}
        onCancel={() => setDrawerRepresentative(undefined)}
      />

      <div className="margin-top-large">
        <ComponentWithPermission permissions={[Permission.AGENT_UPDATE]}>
          <Button className="margin-right-tiny" type="primary" onClick={onUpdate} icon={<AntIcon type="edit" />}>
            {t("common.edit")}
          </Button>
        </ComponentWithPermission>

        <ComponentWithPermission permissions={[Permission.AGENT_DELETE]}>
          <Popconfirm
            title={t("agent.titles.deleteConfirm", { aggregatedName: data.aggregatedName })}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteSecondaryAgent.mutate({ id1: agentId, id2: data.id })}
          >
            <Button icon={<AntIcon type="delete" />} danger>
              {t("common.delete")}
            </Button>
          </Popconfirm>
        </ComponentWithPermission>
      </div>
    </>
  ) : (
    <Empty
      className="margin-top-large"
      description={
        agentSecondaryStatus === AgentSecondaryStatus.AGENT_WITH_CONTRACT
          ? t("agent.helpers.secondaryAgentDataPlaceholder")
          : undefined
      }
    >
      <ComponentWithPermission permissions={[Permission.AGENT_UPDATE]}>
        <Button type="primary" icon={<AntIcon type="plus" />} onClick={onUpdate} className="margin-top-small">
          {t("agent.actions.addData")}
        </Button>
      </ComponentWithPermission>
    </Empty>
  );
};
